import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import NavBar from '../components/navbar';
import Footer from '../components/footer';
import PageHero from '../components/PageHero';
import SectionHeader from '../components/SectionHeader';
import CtaSection from '../components/CtaSection';
import ScrollReveal from '../components/animated/ScrollReveal';
import Text from '../components/Text';
import MagneticButton from '../components/animated/MagneticButton';
import { colors, gradients } from '../theme/designTokens';
import { aeraLayers, aeraMeta } from '../data/aera';
import { useHashScroll } from '../hooks/useHashScroll';

const layerIds = aeraLayers.map((l) => l.id);

const Framework = () => {
  const location = useLocation();
  const hashId = location.hash ? location.hash.slice(1).toLowerCase() : '';
  const initial =
    layerIds.includes(hashId) ? hashId : aeraLayers[0].id;
  const [active, setActive] = useState(initial);
  const navigate = useNavigate();
  const current = aeraLayers.find((l) => l.id === active) || aeraLayers[0];
  useHashScroll();

  useEffect(() => {
    if (layerIds.includes(hashId)) setActive(hashId);
  }, [hashId]);

  return (
    <Box sx={{ bgcolor: colors.white, overflow: 'hidden' }}>
      <NavBar />
      <PageHero
        variant="dark"
        eyebrow={aeraMeta.name}
        title="ALEgION Enterprise"
        titleHighlight="Resilience Architecture"
        subtitle={aeraMeta.summary}
        badges={['Anticipate', 'Assess', 'Architect', 'Activate', 'Advance']}
        minHeight={{ xs: 360, md: 480 }}
      />

      <Box id="aera-layers" sx={{ px: { xs: 2, sm: 3, md: 4 }, py: { xs: 8, md: 12 }, scrollMarginTop: 96 }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <ScrollReveal>
            <SectionHeader
              eyebrow="Proprietary Methodology"
              title="Five layers. One signature model."
              highlightWords={['signature']}
              subtitle="AERA™ appears consistently across our website, proposals, presentations, and thought leadership—so clients associate ALEgION with a recognizable approach to building organizational resilience."
            />
          </ScrollReveal>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' },
              gap: 1.5,
              mb: 5,
            }}
          >
            {aeraLayers.map((layer) => {
              const isActive = layer.id === active;
              return (
                <Box
                  key={layer.id}
                  id={layer.id}
                  component={motion.button}
                  type="button"
                  onClick={() => {
                    setActive(layer.id);
                    navigate(`/framework#${layer.id}`, { replace: true });
                  }}
                  whileHover={{ y: -3 }}
                  sx={{
                    py: 3,
                    px: 2,
                    cursor: 'pointer',
                    border: `1px solid ${isActive ? colors.navy : colors.borderLight}`,
                    background: isActive ? colors.navy : colors.white,
                    textAlign: 'center',
                    scrollMarginTop: 96,
                  }}
                >
                  <Text
                    variant="eyebrow"
                    sx={{ color: isActive ? colors.silver : colors.slate, mb: 1, display: 'block' }}
                  >
                    {layer.number}
                  </Text>
                  <Text variant="h4" sx={{ color: isActive ? colors.white : colors.navy }}>
                    {layer.title}
                  </Text>
                </Box>
              );
            })}
          </Box>

          <AnimatePresence mode="wait">
            <Box
              key={current.id}
              component={motion.div}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              sx={{
                p: { xs: 3, md: 5 },
                background: gradients.sectionDark,
                mb: 6,
              }}
            >
              <Text variant="eyebrow" sx={{ color: colors.ochre, mb: 2, display: 'block' }}>
                {current.number} · {current.title}
              </Text>
              <Text variant="h2" color="white" sx={{ mb: 2 }}>
                {current.subtitle}
              </Text>
              <Text variant="body" color="muted" sx={{ mb: 3, display: 'block', maxWidth: 720 }}>
                {current.description}
              </Text>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 2,
                }}
              >
                {current.details.map((d) => (
                  <Box key={d} sx={{ borderLeft: `2px solid ${colors.silver}`, pl: 2 }}>
                    <Text variant="bodySm" color="muted">
                      {d}
                    </Text>
                  </Box>
                ))}
              </Box>
            </Box>
          </AnimatePresence>

          <ScrollReveal>
            <Box
              sx={{
                p: { xs: 3, md: 4 },
                border: `1px solid ${colors.borderLight}`,
                textAlign: 'center',
                background: colors.offWhite,
              }}
            >
              <Text variant="h3" color="primary" sx={{ mb: 1 }}>
                Resilience Scorecard
              </Text>
              <Text variant="body" color="secondary" sx={{ mb: 3, display: 'block' }}>
                Interactive Risk Maturity Assessment — coming soon. Begin with a conversation about your current resilience posture.
              </Text>
              <MagneticButton variant="navy" onClick={() => navigate('/contact')}>
                Request a Briefing
              </MagneticButton>
            </Box>
          </ScrollReveal>
        </Box>
      </Box>

      <CtaSection
        title="Make AERA™ your operating system for resilience."
        subtitle="From anticipation to advancement—engineered end to end."
      />
      <Footer />
    </Box>
  );
};

export default Framework;
